import {Question} from '~src/interfaces/question.interface';
import {ContentLocalesI} from '~src/interfaces/session.types';
import {Topic, TopicRespI, TopicSubject} from '~src/interfaces/topic.interface';
import {
  ADD_QUESTION_TOPIC_FAILED,
  ADD_QUESTION_TOPIC_REQUEST,
  ADD_QUESTION_TOPIC_SUCCESS,
  ADD_TOPIC_FAILED,
  ADD_TOPIC_REQUEST,
  ADD_TOPIC_SUCCESS,
  DELETE_QUESTION_FAILED,
  DELETE_QUESTION_REQUEST,
  DELETE_QUESTION_SUCCESS,
  DELETE_TOPIC_FAILED,
  DELETE_TOPIC_REQUEST,
  DELETE_TOPIC_SUCCESS,
  EDIT_QUESTION_FAILED,
  EDIT_QUESTION_REQUEST,
  EDIT_QUESTION_SUCCESS,
  EDIT_TOPIC_FAILED,
  EDIT_TOPIC_REQUEST,
  EDIT_TOPIC_SUCCESS,
  FETCH_SINGLE_TOPIC_FAILED,
  FETCH_SINGLE_TOPIC_REQUEST,
  FETCH_SINGLE_TOPIC_SUCCESS,
  GET_ALL_QUESTIONS_FAILED,
  GET_ALL_QUESTIONS_REQUEST,
  GET_ALL_QUESTIONS_SUCCESS,
  GET_TOPIC_QUESTIONS_FAILED,
  GET_TOPIC_QUESTIONS_REQUEST,
  GET_TOPIC_QUESTIONS_SUCCESS,
  GET_TOPICS_FAILED,
  GET_TOPICS_REQUEST,
  GET_TOPICS_SUCCESS,
  RESET_TOPICS,
} from '~src/modules/topics/constants/actionTypes';
import {defaultPulseCheckApi, pulseCheck} from '~src/services/ApiController';
import {AsyncAction} from '~src/services/apiMiddleware';
import {ListResponse} from "~src/services/api/pulseCheck";
import {SortingValues} from "~src/modules/reports/shared/constants/filterAndSortingKeys";

export interface QuestionSubmit {
  title?: { [key: string]: string };
  description?: { [key: string]: string };
  selfTitle?: { [key: string]: string };
  isCommentEnabled: boolean;
}


export type TopicCreate = Partial<Pick<Topic, 'comments'|'description'|'name'|'scaleId'|'subject'>>

// see corresponding UpdateTopic model in pulse-check-service
export type UpdateTopic = Partial<Pick<Topic, 'comments'|'description'|'name'|'scaleId'|'archived'>>


export type TopicSortParam = SortingValues.Created_asc|SortingValues.Created_desc|SortingValues.TopicNames_asc|SortingValues.TopicNames_desc;

export interface FetchTopicsParams {
  'filter.subject'?: TopicSubject;
  'filter.archived'?: boolean;
  include?: 'questions' | 'questions,private';
  page?: number;
  per_page?: number;
  sort?: TopicSortParam;
}

export function fetchTopicsList(params?: FetchTopicsParams): AsyncAction<TopicRespI, undefined> {
  const paramsMerged: FetchTopicsParams = {
    sort: SortingValues.Created_desc,
    ...params,
  };

  return {
    types: [GET_TOPICS_REQUEST, GET_TOPICS_SUCCESS, GET_TOPICS_FAILED],
    callAPI: () => defaultPulseCheckApi.getTopics(paramsMerged),
  };
}

export function fetchTopicById(topicId: string): AsyncAction<Topic, never> {
  return {
    types: [FETCH_SINGLE_TOPIC_REQUEST, FETCH_SINGLE_TOPIC_SUCCESS, FETCH_SINGLE_TOPIC_FAILED],
    callAPI: () => defaultPulseCheckApi.getTopic(topicId),
  };
}

export function fetchAllQuestions(): AsyncAction<ListResponse<Question>, never> {
  return {
    types: [GET_ALL_QUESTIONS_REQUEST, GET_ALL_QUESTIONS_SUCCESS, GET_ALL_QUESTIONS_FAILED],
    callAPI: () => pulseCheck.get('/questions?per_page=10000'),
  };
}

export function addTopic(topic: TopicCreate): AsyncAction<Topic, TopicCreate> {
  return {
    types: [ADD_TOPIC_REQUEST, ADD_TOPIC_SUCCESS, ADD_TOPIC_FAILED],
    callAPI: () => pulseCheck.post('/topics', topic),
    payload: topic,
  };
}

export function deleteTopic(topicId: string, name: ContentLocalesI): AsyncAction<void, { topicId: string, name: ContentLocalesI }> {
  return {
    types: [DELETE_TOPIC_REQUEST, DELETE_TOPIC_SUCCESS, DELETE_TOPIC_FAILED],
    callAPI: () => pulseCheck.delete(`/topics/${topicId}`),
    payload: { topicId, name },
  };
}

export function editTopicPatch(topicId: string, payload: Partial<UpdateTopic>): AsyncAction<Topic, {topicId: string, payload: Partial<UpdateTopic>}> {
  return {
    types: [EDIT_TOPIC_REQUEST, EDIT_TOPIC_SUCCESS, EDIT_TOPIC_FAILED],
    callAPI: () => pulseCheck.patch(`/topics/${topicId}`, payload),
    payload: { topicId, payload },
  };
}

export function archiveTopic(topicId: string): AsyncAction<Topic, undefined> {
  return editTopicPatch(topicId, {archived: true});
}

export function unarchiveTopic(topicId: string): AsyncAction<Topic, undefined> {
  return editTopicPatch(topicId, {archived: false});
}

export function fetchTopicQuestions(topicId: string): AsyncAction<Question[], {topicId: string}> {
  return {
    types: [GET_TOPIC_QUESTIONS_REQUEST, GET_TOPIC_QUESTIONS_SUCCESS, GET_TOPIC_QUESTIONS_FAILED],
    callAPI: () => pulseCheck.get(`/topics/${topicId}/questions`),
    payload: { topicId },
  };
}

export function addTopicQuestion(
  topicId: string,
  question: QuestionSubmit,
  showNotif?: boolean,
): AsyncAction<Question, {topicId: string, question: QuestionSubmit, showNotif?: boolean}> {
  return {
    types: [ADD_QUESTION_TOPIC_REQUEST, ADD_QUESTION_TOPIC_SUCCESS, ADD_QUESTION_TOPIC_FAILED],
    callAPI: () => pulseCheck.post(`/topics/${topicId}/questions`, question),
    payload: { topicId, question, showNotif },
  };
}

export function deleteTopicQuestion(
  topicId: string,
  questionId: string,
  name: ContentLocalesI | null,
): AsyncAction<void, {topicId: string, questionId: string, name: ContentLocalesI| null}> {
  return {
    types: [DELETE_QUESTION_REQUEST, DELETE_QUESTION_SUCCESS, DELETE_QUESTION_FAILED],
    callAPI: () => pulseCheck.delete(`/questions/${questionId}`),
    payload: { topicId, questionId, name },
  };
}

export function editTopicQuestion(questionId: string, payload: QuestionSubmit): AsyncAction<Question, {payload: QuestionSubmit, questionId: string}> {
  return {
    types: [EDIT_QUESTION_REQUEST, EDIT_QUESTION_SUCCESS, EDIT_QUESTION_FAILED],
    callAPI: () => pulseCheck.put(`questions/${questionId}`, payload),
    payload: { payload, questionId },
  };
}

export function resetTopics() {
  return { type: RESET_TOPICS };
}
